import { getTokenBg } from "../lib/token-colors";

import { AnimatedBalance } from "./AnimatedBalance";

interface CompositionBarProps {
  /** Value of token0 in the pool (any unit, just relative) */
  value0: number;
  /** Value of token1 in the pool */
  value1: number;
  /** Label for token0 (may include symbol, e.g. "WETH (50.3%)") */
  label0: string;
  /** Label for token1 */
  label1: string;
  /** Height in px */
  height?: number;
  /** Optional label below the bar (e.g. "TVL: $25.2M") */
  tvlLabel?: string;
}

/** Extract the leading word from a label as the token symbol */
function extractSymbol(label: string): string {
  return label.split(/[\s(]/)[0] ?? "";
}

export function CompositionBar({
  value0,
  value1,
  label0,
  label1,
  height = 8,
  tvlLabel,
}: CompositionBarProps) {
  const total = value0 + value1;

  // Empty pool placeholder
  if (total === 0) {
    return (
      <div className="space-y-0.5">
        <div
          className="flex w-full overflow-hidden rounded-full bg-[#374151]"
          style={{ height }}
        />
        <div className="text-muted-foreground text-center text-[10px]">
          No reserves
        </div>
        {tvlLabel && (
          <div className="text-muted-foreground text-center text-[10px]">
            {tvlLabel}
          </div>
        )}
      </div>
    );
  }

  const pct0 = (value0 / total) * 100;
  const pct1 = 100 - pct0;
  const isOneSided = value0 === 0 || value1 === 0;

  const color0 = value0 === 0 ? "#e8a020" : getTokenBg(extractSymbol(label0));
  const color1 = value1 === 0 ? "#e8a020" : getTokenBg(extractSymbol(label1));

  return (
    <div className="space-y-0.5">
      <div
        className="flex w-full overflow-hidden rounded-full"
        style={{ height }}
        title={`${label0}: ${pct0.toFixed(1)}% | ${label1}: ${pct1.toFixed(1)}%`}
      >
        <div
          className="transition-all"
          style={{ width: `${String(pct0)}%`, backgroundColor: color0 }}
        />
        <div
          className="transition-all"
          style={{ width: `${String(pct1)}%`, backgroundColor: color1 }}
        />
      </div>
      <div className="text-muted-foreground flex justify-between text-[10px]">
        <span>
          {label0}{" "}
          <AnimatedBalance
            value={pct0}
            format={(n) => n.toFixed(1)}
            suffix="%"
            emDash={false}
          />
          {value0 === 0 && isOneSided ? " (fully one-sided)" : ""}
        </span>
        <span>
          {label1}{" "}
          <AnimatedBalance
            value={pct1}
            format={(n) => n.toFixed(1)}
            suffix="%"
            emDash={false}
          />
          {value1 === 0 && isOneSided ? " (fully one-sided)" : ""}
        </span>
      </div>
      {tvlLabel && (
        <div className="text-muted-foreground text-[10px]">{tvlLabel}</div>
      )}
    </div>
  );
}
